import React, { useState, useCallback } from "react";
import { Input } from "reactstrap";
import debounce from "lodash/debounce";
import { isMobile } from "../../../utils/deviceInfo";

// Reusable debounced search input used by the admin trivia tabs (Surveys,
// Messaging, Knowledge Checks). The parent owns the network call and
// pagination; this component only debounces typing and surfaces clear/clear
// events via callbacks. Kept visually consistent with the Library tab search
// but scoped to avoid importing a "Library" component elsewhere in the code.
const LocalSearchBar = ({
  onSearch,
  onClearSearch,
  isSearching,
  placeholder,
  mobilePlaceholder,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const desktopPh = placeholder || "Search by name...";
  const mobilePh = mobilePlaceholder || placeholder || "Search by name...";

  const performSearch = (inputValue) => {
    if (inputValue && inputValue.trim()) {
      onSearch(inputValue);
    } else {
      onClearSearch();
    }
  };

  // 800ms matches the Library tab feel.
  const debouncedSearch = useCallback(
    debounce((value) => {
      performSearch(value);
    }, 800),
    []
  );

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleClear = () => {
    setSearchQuery("");
    onClearSearch();
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <Input
        type="text"
        className="bwai-search-input"
        placeholder={isMobile ? mobilePh : desktopPh}
        value={searchQuery}
        onChange={handleInputChange}
        // Values mirror the trivvy-list-bwai.css tokens (TBV-853):
        // --surface-subtle #f8f9fb, --border-card #e5e8ee,
        // --text-muted #64748b, --text-primary #0f172a. They are literals
        // rather than var() because the focus/blur handlers below assign to
        // element.style directly, and keeping both halves in one notation is
        // what stops them drifting apart.
        style={{
          // Page surface, not a tinted fill: the Figma has this box read as an
          // outline on the page. Blur handler doesn't touch background, so no drift.
          backgroundColor: "#fbfcfd",
          // Transparent, not removed: the focus handler colours THIS border as the
          // focus indicator, and keeping 1px reserves the box so focus cannot shift layout.
          border: "1px solid transparent",
          borderRadius: "8px",
          fontSize: "14px",
          color: "#0f172a",
          padding: isMobile ? "10px 40px 10px 40px" : "8px 40px 8px 36px",
          width: "100%",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
          outline: "none",
        }}
        onFocus={(e) => {
          // --text-muted. This border IS the focus indicator: `outline: none`
          // above removes the native one, and the 10% ring is far too faint to
          // carry it. #64748b measures 4.76:1 against white and 3.88:1 against
          // the resting #e5e8ee border, clearing WCAG 1.4.11 (3:1). A lighter
          // neutral such as #b8c0cc drops to 1.83:1 and effectively hides
          // keyboard focus — the previous blue was 3.68:1, so it must not
          // regress below that.
          e.target.style.borderColor = "#7b8a9c";
          e.target.style.boxShadow = "0 0 0 3px rgba(123,138,156,0.10)";
        }}
        onBlur={(e) => {
          // Must match the resting values above — otherwise the input keeps
          // the old border colour for the rest of the session after one focus.
          e.target.style.borderColor = "transparent";
          e.target.style.boxShadow = "0 1px 3px rgba(15, 23, 42, 0.08)";
        }}
      />

      {/* Search icon / loading spinner */}
      <span
        style={{
          position: "absolute",
          left: isMobile ? "14px" : "12px",
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
        }}
      >
        {isSearching ? (
          <div
            className="spinner-border spinner-border-sm"
            role="status"
            style={{
              width: "16px",
              height: "16px",
              borderWidth: "2px",
              // --text-muted. Was #3b82f6, the last blue left in an otherwise
              // neutral input once the field and focus ring were restyled.
              color: "#64748b",
            }}
          >
            <span className="sr-only">Searching...</span>
          </div>
        ) : (
          <i
            className="flaticon-search"
            style={{
              color: "#9ca3af",
              fontSize: "16px",
            }}
          />
        )}
      </span>

      {/* Clear button */}
      {searchQuery && (
        <span
          onClick={handleClear}
          style={{
            position: "absolute",
            right: isMobile ? "14px" : "12px",
            top: "50%",
            transform: "translateY(-50%)",
            color: "#6b7280",
            fontSize: "18px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          ×
        </span>
      )}
    </div>
  );
};

export default LocalSearchBar;
